// OPS-AUTH (2026-10-29): the platform's ops automation account. Maintenance scripts
// (`infra/ops/*.ts`) act through it and through nothing else: never a person's password, never a
// password reset, never a session as a real person (Andrei's rule, 2026-10-29).
//
// The account is a service account, not a person: a reserved address under `.invalid` (RFC 2606:
// no mail is ever delivered there), no password and no other sign-in method, an agency user of
// the issuing super admin's agency (role `operator`) with an `agency_operator` tuple on each
// company the agency holds a live operator grant on. Every write it makes is recorded as
// `agency_user` with its own user id. Scripts on the host open its session in process only.
//
// Platform super admins only (as Connections): issue (idempotent; also reaches companies granted
// since) and revoke (tuples, the agency row and every open session of the account). Both are a
// `cp.platform_audit` row (`ops_account.issued` / `ops_account.revoked`) and the companies'
// `access.granted` / `access.revoked` events.
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { defineRoute } from "./route";

const base = "/v1/operator/ops-account";
const core = { feature: "core", tag: "operator", access: "operator" } as const;

export const OpsAccountView = z
  .object({
    /** The reserved address (never a person's; nothing is delivered to `.invalid`). */
    email: z.string(),
    /** The account's user id, null until first issued. */
    user_id: Uuid.nullable(),
    /** Issued and not revoked: the account is an agency user now. */
    issued: z.boolean(),
    issued_at: Timestamp.nullable(),
    /** Companies the account may act in (an `agency_operator` tuple held). */
    companies: z.array(z.object({ id: Uuid, slug: z.string(), name: z.string() })),
    /** Open sessions (scripts running now, or not yet signed out). */
    open_sessions: z.int().min(0),
  })
  .meta({ id: "OpsAccountView" });
export type OpsAccountView = z.infer<typeof OpsAccountView>;

export const OpsAccountIssue = z.object({}).strict().meta({ id: "OpsAccountIssue" });
export const OpsAccountRevoke = z.object({}).strict().meta({ id: "OpsAccountRevoke" });

export const opsAccountRoutes = {
  "operator.opsAccount.get": defineRoute({
    ...core,
    id: "operator.opsAccount.get",
    method: "get",
    path: base,
    summary: "The ops automation account: issued or not, and the companies it may act in",
    response: { status: 200, schema: OpsAccountView },
    errors: [],
    emits: [],
  }),
  "operator.opsAccount.issue": defineRoute({
    ...core,
    id: "operator.opsAccount.issue",
    method: "post",
    path: `${base}/issue`,
    summary: "Issue the ops automation account (a service account, no password; idempotent)",
    body: OpsAccountIssue,
    response: { status: 200, schema: OpsAccountView },
    errors: ["conflict"],
    emits: ["access.granted"],
  }),
  "operator.opsAccount.revoke": defineRoute({
    ...core,
    id: "operator.opsAccount.revoke",
    method: "post",
    path: `${base}/revoke`,
    summary: "Revoke the ops automation account: its access, agency row and open sessions",
    body: OpsAccountRevoke,
    response: { status: 200, schema: OpsAccountView },
    errors: [],
    emits: ["access.revoked"],
  }),
};
